import React from "react";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Collapse from "@material-ui/core/Collapse";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Close from "@material-ui/icons/Close";
import ExpandLess from "@material-ui/icons/ExpandLess";

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const GET_CUSTOMER_QUERY = `
  query GetCustomer($id: String!) {
    getCustomer(id: $id) {
      id customerNumber name phone1 phone2 birthday gender email facebook avatar
      address location ward customerGroup note
      customerType buyerName taxRegNumber invoiceAddress invoiceProvince invoiceWard
      identificationNumber passportNumber invoiceEmail invoicePhone bank bankAccountNumber
      isActive
    }
  }
`;

const UPDATE_MUTATION = `
  mutation CreateOrUpdateCustomer($data: CreateCustomerDto!) {
    createOrUpdateCustomer(data: $data)
  }
`;

const BANKS = [
  "Vietcombank (VCB)", "Techcombank (TCB)", "ACB", "BIDV",
  "Vietinbank (VTB)", "MB Bank", "TPBank", "VPBank", "Sacombank (STB)", "Other",
];

const useStyles = makeStyles({
  paper: {
    width: 680,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: "18px", fontWeight: 600 },
  body: {
    padding: "20px 24px",
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "14px 24px",
    borderTop: "1px solid #e0e0e0",
    flexShrink: 0,
  },
  row: {
    display: "flex",
    gap: "12px",
    marginBottom: "14px",
    alignItems: "flex-start",
  },
  field: { flex: 1 },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: "14px",
    marginTop: "4px",
    cursor: "pointer",
    userSelect: "none",
  },
  sectionTitle: { fontWeight: 600, fontSize: "14px" },
  avatarWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
    width: "90px",
  },
  avatarCircle: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    border: "2px dashed #bbb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    color: "#888",
    cursor: "pointer",
    textAlign: "center",
    padding: "8px",
    boxSizing: "border-box",
  },
  avatarHint: {
    fontSize: "11px",
    color: "#999",
    marginTop: "6px",
    textAlign: "center",
    lineHeight: 1.3,
  },
  loadingCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
});

const EMPTY = {
  id: "", customerNumber: "", name: "", phone1: "", phone2: "",
  birthday: "", gender: "", email: "", facebook: "",
  address: "", location: "", ward: "",
  customerGroup: "", note: "",
  customerType: "INDIVIDUAL", buyerName: "", taxRegNumber: "",
  invoiceAddress: "", invoiceProvince: "", invoiceWard: "",
  identificationNumber: "", passportNumber: "",
  invoiceEmail: "", invoicePhone: "",
  bank: "", bankAccountNumber: "",
  isActive: true,
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
}

export default function EditCustomerDrawer({ customerId, onClose, onSaved }) {
  const classes = useStyles();
  const [form, setForm] = React.useState(EMPTY);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [sections, setSections] = React.useState({
    address: true,
    group: true,
    invoice: true,
  });

  const isNew = customerId === "new";

  React.useEffect(() => {
    if (!customerId) return;
    setError("");
    if (isNew) {
      setForm({ ...EMPTY });
      return;
    }
    setLoading(true);
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({
        query: GET_CUSTOMER_QUERY,
        variables: { id: customerId },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
        } else {
          const c = data.getCustomer;
          setForm({
            id: c.id,
            customerNumber: c.customerNumber || "",
            name: c.name || "",
            phone1: c.phone1 || "",
            phone2: c.phone2 || "",
            birthday: c.birthday ? c.birthday.slice(0, 10) : "",
            gender: c.gender || "",
            email: c.email || "",
            facebook: c.facebook || "",
            address: c.address || "",
            location: c.location || "",
            ward: c.ward || "",
            customerGroup: c.customerGroup || "",
            note: c.note || "",
            customerType: c.customerType || "INDIVIDUAL",
            buyerName: c.buyerName || "",
            taxRegNumber: c.taxRegNumber || "",
            invoiceAddress: c.invoiceAddress || "",
            invoiceProvince: c.invoiceProvince || "",
            invoiceWard: c.invoiceWard || "",
            identificationNumber: c.identificationNumber || "",
            passportNumber: c.passportNumber || "",
            invoiceEmail: c.invoiceEmail || "",
            invoicePhone: c.invoicePhone || "",
            bank: c.bank || "",
            bankAccountNumber: c.bankAccountNumber || "",
            isActive: c.isActive,
          });
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, [customerId]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleSection = (key) =>
    setSections((s) => ({ ...s, [key]: !s[key] }));

  const handleSave = () => {
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      id: isNew ? null : form.id,
      birthday: form.birthday || null,
      gender: form.gender || null,
      bank: form.bank || null,
      customerType: form.customerType || null,
    };
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({
        query: UPDATE_MUTATION,
        variables: { data: payload },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
        } else if (data.createOrUpdateCustomer) {
          onSaved && onSaved(isNew ? "Customer created successfully!" : "Customer updated successfully!");
          onClose();
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setSaving(false));
  };

  const chevron = (open) => (
    <ExpandLess
      style={{
        transform: open ? "rotate(0deg)" : "rotate(180deg)",
        transition: "transform 0.2s",
        fontSize: "20px",
        color: "#555",
      }}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={!!customerId}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      {/* Header */}
      <div className={classes.header}>
        <h3 className={classes.title}>{isNew ? "Add customer" : "Edit customer"}</h3>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </div>

      {/* Body */}
      {loading ? (
        <div className={classes.loadingCenter}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.body}>
          {error && (
            <p style={{ color: "#f44336", marginBottom: "12px" }}>{error}</p>
          )}

          {/* Row 1: name + number + avatar */}
          <div className={classes.row}>
            <TextField
              label="Customer name"
              value={form.name}
              onChange={set("name")}
              variant="outlined"
              size="small"
              className={classes.field}
            />
            <TextField
              label="Customer number"
              value={form.customerNumber}
              onChange={set("customerNumber")}
              variant="outlined"
              size="small"
              className={classes.field}
            />
            <div className={classes.avatarWrap}>
              <div className={classes.avatarCircle}>Add image</div>
              <div className={classes.avatarHint}>Up to 2 MB per image</div>
            </div>
          </div>

          {/* Row 2: phone1 + phone2 + birthday + gender */}
          <div className={classes.row}>
            <TextField
              label="Phone 1"
              value={form.phone1}
              onChange={set("phone1")}
              variant="outlined"
              size="small"
              className={classes.field}
            />
            <TextField
              label="Phone 2"
              value={form.phone2}
              onChange={set("phone2")}
              variant="outlined"
              size="small"
              className={classes.field}
            />
            <TextField
              label="Birthday"
              type="date"
              value={form.birthday}
              onChange={set("birthday")}
              variant="outlined"
              size="small"
              className={classes.field}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Gender"
              value={form.gender}
              onChange={set("gender")}
              variant="outlined"
              size="small"
              select
              className={classes.field}
            >
              <MenuItem value="">Select gender</MenuItem>
              <MenuItem value="MALE">Male</MenuItem>
              <MenuItem value="FEMALE">Female</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>

            </TextField>
          </div>

          {/* Row 3: email + facebook */}
          <div className={classes.row}>
            <TextField
              label="Email"
              value={form.email}
              onChange={set("email")}
              variant="outlined"
              size="small"
              className={classes.field}
              placeholder="email@gmail.com"
            />
            <TextField
              label="Facebook"
              value={form.facebook}
              onChange={set("facebook")}
              variant="outlined"
              size="small"
              className={classes.field}
              placeholder="facebook.com/username"
            />
          </div>

          {/* ── Active status ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
            <Switch
              checked={!!form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              color="primary"
            />
            <span style={{ fontSize: "14px", fontWeight: 500, color: form.isActive ? "#2e7d32" : "#9e9e9e" }}>
              {form.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* ── Address ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("address")}
          >
            <span className={classes.sectionTitle}>Address</span>
            {chevron(sections.address)}
          </div>
          <Collapse in={sections.address}>
            <div className={classes.row}>
              <TextField
                label="Address"
                value={form.address}
                onChange={set("address")}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </div>
            <div className={classes.row}>
              <TextField
                label="Location"
                value={form.location}
                onChange={set("location")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Choose Province/District"
              />
              <TextField
                label="Ward"
                value={form.ward}
                onChange={set("ward")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Choose Ward"
              />
            </div>
          </Collapse>

          {/* ── Customer group, note ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("group")}
          >
            <span className={classes.sectionTitle}>Customer group, note</span>
            {chevron(sections.group)}
          </div>
          <Collapse in={sections.group}>
            <div className={classes.row}>
              <TextField
                label="Customer group"
                value={form.customerGroup}
                onChange={set("customerGroup")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Select customer group"
              />
            </div>
            <div className={classes.row}>
              <TextField
                label="Note"
                value={form.note}
                onChange={set("note")}
                variant="outlined"
                size="small"
                className={classes.field}
                multiline
                rows={3}
                placeholder="Enter note"
              />
            </div>
          </Collapse>

          {/* ── Invoice information ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("invoice")}
          >
            <span className={classes.sectionTitle}>Invoice information</span>
            {chevron(sections.invoice)}
          </div>
          <Collapse in={sections.invoice}>
            <div style={{ marginBottom: "14px" }}>
              <span style={{ fontSize: "13px", fontWeight: 500, marginRight: "8px" }}>
                Customer type
              </span>
              <RadioGroup
                row
                value={form.customerType}
                onChange={set("customerType")}
              >
                <FormControlLabel
                  value="INDIVIDUAL"
                  control={<Radio size="small" color="primary" />}
                  label="Individual"
                />
                <FormControlLabel
                  value="ORGANIZATION"
                  control={<Radio size="small" color="primary" />}
                  label="Organization/Business"
                />
              </RadioGroup>
            </div>

            <div className={classes.row}>
              <TextField
                label="Buyer's Name"
                value={form.buyerName}
                onChange={set("buyerName")}
                variant="outlined"
                size="small"
                className={classes.field}
              />
              <TextField
                label="Tax reg. number"
                value={form.taxRegNumber}
                onChange={set("taxRegNumber")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Enter tax reg. number"
              />
            </div>

            <div className={classes.row}>
              <TextField
                label="Address"
                value={form.invoiceAddress}
                onChange={set("invoiceAddress")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Enter address"
              />
            </div>

            <div className={classes.row}>
              <TextField
                label="Province/City"
                value={form.invoiceProvince}
                onChange={set("invoiceProvince")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Search Province/City"
              />
              <TextField
                label="Ward/Commune"
                value={form.invoiceWard}
                onChange={set("invoiceWard")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Search Ward/Commune"
              />
            </div>

            <div className={classes.row}>
              <TextField
                label="Identification Number"
                value={form.identificationNumber}
                onChange={set("identificationNumber")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Enter identification number"
              />
              <TextField
                label="Passport Number"
                value={form.passportNumber}
                onChange={set("passportNumber")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Enter passport number"
              />
            </div>

            <div className={classes.row}>
              <TextField
                label="Email"
                value={form.invoiceEmail}
                onChange={set("invoiceEmail")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="email@gmail.com"
              />
              <TextField
                label="Phone Number"
                value={form.invoicePhone}
                onChange={set("invoicePhone")}
                variant="outlined"
                size="small"
                className={classes.field}
              />
            </div>

            <div className={classes.row}>
              <TextField
                label="Bank"
                value={form.bank}
                onChange={set("bank")}
                variant="outlined"
                size="small"
                select
                className={classes.field}
              >
                <MenuItem value="">Select bank</MenuItem>
                {BANKS.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Bank Account Number"
                value={form.bankAccountNumber}
                onChange={set("bankAccountNumber")}
                variant="outlined"
                size="small"
                className={classes.field}
                placeholder="Enter Bank Account Number"
              />
            </div>
          </Collapse>
        </div>
      )}

      {/* Footer */}
      <div className={classes.footer}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </div>
    </Drawer>
  );
}
